<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ tradeItem ? $t('HeadContract.IndirectBillOfQuantities.TradeItemSaveModal.update') : $t('HeadContract.IndirectBillOfQuantities.TradeItemSaveModal.create') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="h-full w-full overflow-y-auto">
        <form
          action=""
          class="flex flex-col space-y-4"
          @submit.prevent="saveTrade"
        >
          <div>
            <p class="text-lg font-semibold">
              {{ tradeItem ? $t('HeadContract.IndirectBillOfQuantities.TradeItemSaveModal.under', { trade: trade.name }) : $t('HeadContract.IndirectBillOfQuantities.TradeItemSaveModal.addingUnder', { trade: trade.name }) }}
            </p>
          </div>

          <label>
            {{ $t('HeadContract.IndirectBillOfQuantities.TradeItemSaveModal.label.description') }}
            <AppTextArea
              v-model="tradeItemForm.form.name"
              name="name"
              type="text"
              class="form-input resize-none"
              :error="tradeItemForm.getErrors('name')"
              data-test="name-input"
              rows="5"
            />
          </label>

          <div class="flex space-x-4">
            <label>
              {{ $t('HeadContract.IndirectBillOfQuantities.TradeItemSaveModal.label.unit') }}

              <UnitsTypeAhead
                v-model="tradeItemForm.form.unit"
                name="unit"
                :error="tradeItemForm.getErrors('unit')"
              />
            </label>

            <label>
              {{ $t('HeadContract.IndirectBillOfQuantities.TradeItemSaveModal.label.quantity') }}
              <AppInput
                v-model="tradeItemForm.form.quantity"
                class="form-input"
                name="quantity"
                type="number"
                :error="tradeItemForm.getErrors('quantity')"
                data-test="quantity-input"
              />
            </label>
          </div>

          <label>
            {{ $t('HeadContract.IndirectBillOfQuantities.TradeItemSaveModal.label.rate') }} <span class="text-sm text-gray-400">({{ $t('currency.symbol') }}/{{ $t('HeadContract.IndirectBillOfQuantities.TradeItemSaveModal.hint.unit') }})</span>

            <AppCurrencyInput
              v-model="tradeItemForm.form.rate"
              class="form-input"
              name="rate"
              :error="tradeItemForm.getErrors('rate')"
              data-test="rate-input"
            />
          </label>

          <label>
            {{ $t('HeadContract.IndirectBillOfQuantities.TradeItemSaveModal.label.budget') }} <span class="text-sm text-gray-400">({{ $t('currency.symbol') }})</span>

            <AppCurrencyInput
              v-model="tradeItemForm.form.budget"
              class="form-input disabled:bg-gray-300"
              name="budget"
              :error="tradeItemForm.getErrors('budget')"
              :options="{
                currency: 'AUD',
                currencyDisplay: CurrencyDisplay.narrowSymbol,
                hideGroupingSeparatorOnFocus: false
              }"
              data-test="budget-input"
              disabled
            />
          </label>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close'); tradeItemForm.resetData()"
        >
          {{ $t('HeadContract.IndirectBillOfQuantities.TradeItemSaveModal.button.cancel') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          :disabled="createTradeItemAction.is(States.LOADING)"
          data-test="save-trade-item"
          @click="saveTrade"
        >
          {{ tradeItem ? $t('HeadContract.IndirectBillOfQuantities.TradeItemSaveModal.button.update') : $t('HeadContract.IndirectBillOfQuantities.TradeItemSaveModal.button.create') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import UnitsTypeAhead from '@/components/UnitsTypeAhead.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useTradeItemsStore } from '@/store/tradeItems';
import Swal from 'sweetalert2';
import { useTemplateRef, watch } from 'vue';
import { CurrencyDisplay } from 'vue-currency-input';

const props = defineProps<{
  trade: Trade;
  tradeItem?: TradeItem;
  selectedPosition?: number;
}>();

const emits = defineEmits<{
  (e: 'close');
  (e: 'updateSort', data: { tradeItem: TradeItem; index: number });
}>();

const tradeItemForm = useFormData({
  name: '',
  budget: 0,
  trade_id: props.trade.id,
  rate: 0,
  quantity: 0,
  unit: '',
  sort_order: undefined,
  test_currency: 0,
});

watch(() => props.selectedPosition, (newValue) => {
  tradeItemForm.form.sort_order = newValue;
}, { immediate: true });

watch(() => [tradeItemForm.form.rate, tradeItemForm.form.quantity], (newValue) => {
  if(newValue[0] > 0 && newValue[1] > 0) {
    tradeItemForm.form.budget = newValue[0] * newValue[1];
  }
});

watch(() => props.tradeItem, (newValue) => {
  if(newValue) {
    tradeItemForm.setData(newValue);
  }
}, { immediate: true });

const tradeItemsStore = useTradeItemsStore();
const createTradeItemAction = useStoreApiAction(tradeItemsStore.createTradeItem);
const updateTradeItemAction = useStoreApiAction(tradeItemsStore.updateTradeItem);

const saveTrade = () => {
  tradeItemForm.resetErrors();

  if(props.tradeItem) {
    updateTradeItemAction.request(props.tradeItem.id, tradeItemForm.form).then(() => {
      Swal.fire({
        icon: 'success',
        title: 'Trade item updated.',
      }).then(() => {
        emits('close');
      });
    }).catch((error) => {
      tradeItemForm.setErrors(error.data);
    });
  } else {
    createTradeItemAction.request(props.trade.project_id, tradeItemForm.form).then((data) => {
      emits('updateSort', { tradeItem: data, index: data.sort_order });

      Swal.fire({
        icon: 'success',
        title: 'Trade item created.',
      }).then(() => {
        emits('close');
      });
    }).catch((error) => {
      tradeItemForm.setErrors(error.data);
    });
  }
};
</script>

<style scoped></style>
